// Row type order and labels for Halton Region Roads Report Excel
const HALTON_REPORT_ROW_KEYS = ["emergency", "standard", "largeExcavation"];
const HALTON_REPORT_ROW_LABELS = {
  emergency: "Emergency",
  standard: "Standard",
  largeExcavation: "Large Excavation",
};

const TICKET_COLUMNS = [
  "Ticket No",
  "Station Codes",
  "Company Name",
  "Contractor Name",
  "Address",
  "City",
  "Entered Date",
  "Completed Date",
  "Pre Completed Date",
  "Priority",
  "Ticket Type",
  "Marked Units",
  "Clear Units",
  "Additional Meters",
  "Utility Type",
];

/** Water office clear: new API uses an array of { stationCode, totalCount, tickets }. Legacy: { totalCount, tickets }. */
function formatWaterOfficeClearTotal(officeClear) {
  if (officeClear == null) return "";
  if (Array.isArray(officeClear)) {
    return [...officeClear]
      .filter((e) => e && typeof e === "object")
      .sort((a, b) =>
        String(a.stationCode ?? "").localeCompare(String(b.stationCode ?? "")),
      )
      .map((e) => `${e.stationCode ?? "?"}: ${e.totalCount ?? 0}`)
      .join("; ");
  }
  if (typeof officeClear === "object" && "totalCount" in officeClear) {
    return officeClear.totalCount ?? "";
  }
  return "";
}

function pushWaterOfficeClearTicketSections(rows, labelPrefix, officeClear) {
  if (officeClear == null) return;
  if (Array.isArray(officeClear)) {
    officeClear.forEach((block) => {
      if (!block || typeof block !== "object") return;
      const code = block.stationCode ?? "";
      pushOfficeClearTicketRows(
        rows,
        `${labelPrefix} - Office Clear (${code}) - Ticket Details`,
        block.tickets,
      );
    });
    return;
  }
  if (officeClear.tickets) {
    pushOfficeClearTicketRows(
      rows,
      `${labelPrefix} - Office Clear - Ticket Details`,
      officeClear.tickets,
    );
  }
}

/** Pushes rows for office clear tickets (plain array). Section appears above Field Marked/Field Cleared ticket details. */
function pushOfficeClearTicketRows(rows, sectionTitle, ticketsArray) {
  if (!Array.isArray(ticketsArray) || ticketsArray.length === 0) return;
  const emptyTail = Array(Math.max(0, TICKET_COLUMNS.length - 1)).fill("");
  rows.push([]);
  rows.push([sectionTitle, ...emptyTail]);
  rows.push(TICKET_COLUMNS.slice());
  ticketsArray.forEach((t) => {
    rows.push([
      t.ticketNo ?? "",
      Array.isArray(t.stationCodes)
        ? t.stationCodes.join(", ")
        : (t.stationCodes ?? ""),
      t.company_name ?? "",
      t.contractor_name ?? t.contractorname ?? "",
      t.address ?? "",
      t.city ?? "",
      t.created_at ?? "",
      t.completed_date ?? "",
      t.pre_completed_date ?? "",
      t.priority ?? "",
      t.ticket_type ?? t.type ?? "",
      t.markedunits ?? "",
      t.clearunits ?? "",
      t.additionalmeters ?? "",
      t.utilityType ?? "",
    ]);
  });
}

/** Pushes rows for one tickets block (e.g. fieldMarked.tickets) with sectionTitle. */
function pushTicketDetailRows(rows, sectionTitle, ticketsObj) {
  if (!ticketsObj || typeof ticketsObj !== "object") return;
  const categories = ["emergency", "standard", "largeExcavation"];
  let hasAny = false;
  categories.forEach((key) => {
    const list = ticketsObj[key];
    if (!Array.isArray(list) || list.length === 0) return;
    if (!hasAny) {
      const emptyTail = Array(Math.max(0, TICKET_COLUMNS.length - 1)).fill("");
      rows.push([]);
      rows.push([sectionTitle, ...emptyTail]);
      hasAny = true;
    }
    rows.push([
      HALTON_REPORT_ROW_LABELS[key],
      ...Array(Math.max(0, TICKET_COLUMNS.length - 1)).fill(""),
    ]);
    rows.push(TICKET_COLUMNS.slice());
    list.forEach((t) => {
      rows.push([
        t.ticketNo ?? "",
        Array.isArray(t.stationCodes)
          ? t.stationCodes.join(", ")
          : (t.stationCodes ?? ""),
        t.company_name ?? "",
        t.contractor_name ?? t.contractorname ?? "",
        t.address ?? "",
        t.city ?? "",
        t.created_at ?? "",
        t.completed_date ?? "",
        t.pre_completed_date ?? "",
        t.priority ?? "",
        t.ticket_type ?? t.type ?? "",
        t.markedunits ?? "",
        t.clearunits ?? "",
        t.additionalmeters ?? "",
        t.utilityType ?? "",
      ]);
    });
  });
}

/** Field-marked/cleared ticket details grouped by utility key (roads + water; same row shape as pushTicketDetailRows). */
function pushTicketDetailRowsByUtility(rows, sectionTitle, ticketsByUtility) {
  if (!ticketsByUtility || typeof ticketsByUtility !== "object") return;
  let hasSection = false;
  Object.keys(ticketsByUtility).forEach((utilityLabel) => {
    const ticketsObj = ticketsByUtility[utilityLabel];
    if (!ticketsObj || typeof ticketsObj !== "object") return;
    const categoriesWithData = HALTON_REPORT_ROW_KEYS.filter(
      (key) => Array.isArray(ticketsObj[key]) && ticketsObj[key].length > 0,
    );
    if (categoriesWithData.length === 0) return;

    if (!hasSection) {
      const emptyTail = Array(Math.max(0, TICKET_COLUMNS.length - 1)).fill("");
      rows.push([]);
      rows.push([sectionTitle, ...emptyTail]);
      hasSection = true;
    }

    const uTail = Array(Math.max(0, TICKET_COLUMNS.length - 1)).fill("");
    rows.push([utilityLabel, ...uTail]);

    categoriesWithData.forEach((key) => {
      const list = ticketsObj[key];
      rows.push([
        HALTON_REPORT_ROW_LABELS[key],
        ...Array(Math.max(0, TICKET_COLUMNS.length - 1)).fill(""),
      ]);
      rows.push(TICKET_COLUMNS.slice());
      list.forEach((t) => {
        rows.push([
          t.ticketNo ?? "",
          Array.isArray(t.stationCodes)
            ? t.stationCodes.join(", ")
            : (t.stationCodes ?? ""),
          t.company_name ?? "",
          t.contractor_name ?? t.contractorname ?? "",
          t.address ?? "",
          t.city ?? "",
          t.created_at ?? "",
          t.completed_date ?? "",
          t.pre_completed_date ?? "",
          t.priority ?? "",
          t.ticket_type ?? t.type ?? "",
          t.markedunits ?? "",
          t.clearunits ?? "",
          t.additionalmeters ?? "",
          t.utilityType ?? "",
        ]);
      });
    });
  });
}

export const buildHaltonRegionRoadsReportExcel = (data) => {
  const pad = (arr, len, fill = "") =>
    arr.length >= len ? arr : [...arr, ...Array(len - arr.length).fill(fill)];

  const normalizeSheetRows = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return [[""]];
    const maxCols = Math.max(
      9,
      7,
      ...rows.map((r) => (Array.isArray(r) ? r.length : 0)),
    );
    return rows.map((r) => pad(Array.isArray(r) ? r : [r], maxCols));
  };

  const findUtilityByKey = (utilities, key) => {
    if (!Array.isArray(utilities)) return undefined;
    return utilities.find((u) => {
      const name = u?.utility ?? "";
      const codes = Array.isArray(u?.stationCodes)
        ? u.stationCodes.join(",")
        : "";
      return `${name}|${codes}` === key;
    });
  };

  const pushRoadsSummary = (rows, roads, title) => {
    if (!roads) return;
    rows.push([title, "", "", "", "", ""]);
    rows.push([
      "",
      "FIELD MARKED",
      "FIELD MARKED",
      "FIELD CLEARED",
      "FIELD CLEARED",
      "OFFICE CLEAR",
    ]);
    rows.push([
      "",
      "Unit Count",
      "Additional Meters",
      "Unit Count",
      "Additional Meters",
      "OFFICE CLEAR (All station codes for roads)",
    ]);
    const fm = roads.fieldMarked;
    const fc = roads.fieldCleared;

    // Pair utilities by stable key (utility + stationCodes) to avoid index-mismatch issues.
    const fmUtils = Array.isArray(fm?.utilities) ? fm.utilities : [];
    const fcUtils = Array.isArray(fc?.utilities) ? fc.utilities : [];
    const fcMap = new Map(
      fcUtils.map((u) => [
        `${u?.utility ?? ""}|${
          Array.isArray(u?.stationCodes) ? u.stationCodes.join(",") : ""
        }`,
        u,
      ]),
    );

    fmUtils.forEach((fu) => {
      const key = `${fu?.utility ?? ""}|${
        Array.isArray(fu?.stationCodes) ? fu.stationCodes.join(",") : ""
      }`;
      const cu = fcMap.get(key) ?? findUtilityByKey(fcUtils, key);
      const headerLabel =
        (fu?.utility ?? "") +
        (fu?.stationCodes?.length
          ? " - " + (fu.stationCodes || []).join(", ")
          : "");
      rows.push([headerLabel, "", "", "", "", ""]);
      HALTON_REPORT_ROW_KEYS.forEach((rowKey) => {
        const fr = fu?.rows?.[rowKey];
        const cr = cu?.rows?.[rowKey];
        rows.push([
          HALTON_REPORT_ROW_LABELS[rowKey],
          fr?.unitSum ?? "",
          fr?.additionalMeters ?? "",
          cr?.unitSum ?? "",
          cr?.additionalMeters ?? "",
          "",
        ]);
      });
      rows.push([
        "Sub Total",
        fu?.subTotal?.unitSum ?? "",
        fu?.subTotal?.additionalMeters ?? "",
        cu?.subTotal?.unitSum ?? "",
        cu?.subTotal?.additionalMeters ?? "",
        "",
      ]);
    });

    rows.push([
      "TOTAL",
      fm?.total?.unitSum ?? "",
      fm?.total?.additionalMeters ?? "",
      fc?.total?.unitSum ?? "",
      fc?.total?.additionalMeters ?? "",
      roads.officeClear?.totalCount ?? "",
    ]);
  };

  const pushWaterSummary = (rows, water, title) => {
    if (!water) return;
    const waterDesc =
      "HLTNW02(water only, wastewater only) HLTNT01(Water/wastewater Telecommunications)";
    rows.push([title, "", "", "", "", "", ""]);
    rows.push([
      "",
      "FIELD MARKED",
      "FIELD MARKED",
      "FIELD CLEARED",
      "FIELD CLEARED",
      "OFFICE CLEAR",
      "CURB BOX",
    ]);
    rows.push([
      "",
      "Unit Count",
      "Additional Meters",
      "Unit Count",
      "Additional Meters",
      waterDesc,
      waterDesc,
    ]);
    const wfm = water.fieldMarked;
    const wfc = water.fieldCleared;

    const fmUtils = Array.isArray(wfm?.utilities) ? wfm.utilities : [];
    const fcUtils = Array.isArray(wfc?.utilities) ? wfc.utilities : [];
    const fcMap = new Map(
      fcUtils.map((u) => [
        `${u?.utility ?? ""}|${
          Array.isArray(u?.stationCodes) ? u.stationCodes.join(",") : ""
        }`,
        u,
      ]),
    );

    fmUtils.forEach((fu) => {
      const key = `${fu?.utility ?? ""}|${
        Array.isArray(fu?.stationCodes) ? fu.stationCodes.join(",") : ""
      }`;
      const cu = fcMap.get(key) ?? findUtilityByKey(fcUtils, key);
      const headerLabel =
        (fu?.utility ?? "") +
        (fu?.stationCodes?.length
          ? " - " + (fu.stationCodes || []).join(", ")
          : "");
      rows.push([headerLabel, "", "", "", "", "", ""]);
      HALTON_REPORT_ROW_KEYS.forEach((rowKey) => {
        const fr = fu?.rows?.[rowKey];
        const cr = cu?.rows?.[rowKey];
        rows.push([
          HALTON_REPORT_ROW_LABELS[rowKey],
          fr?.unitSum ?? "",
          fr?.additionalMeters ?? "",
          cr?.unitSum ?? "",
          cr?.additionalMeters ?? "",
          "",
          "",
        ]);
      });
      rows.push([
        "Sub Total",
        fu?.subTotal?.unitSum ?? "",
        fu?.subTotal?.additionalMeters ?? "",
        cu?.subTotal?.unitSum ?? "",
        cu?.subTotal?.additionalMeters ?? "",
        "",
        "",
      ]);
    });
    rows.push([
      "TOTAL",
      wfm?.total?.unitSum ?? "",
      wfm?.total?.additionalMeters ?? "",
      wfc?.total?.unitSum ?? "",
      wfc?.total?.additionalMeters ?? "",
      formatWaterOfficeClearTotal(water.officeClear),
      water.curbBox?.totalCount ?? "",
    ]);
  };

  const pushTicketDetails = (
    rows,
    labelPrefix,
    section,
    { isWater = false } = {},
  ) => {
    if (!section) return;
    if (isWater) {
      pushWaterOfficeClearTicketSections(
        rows,
        labelPrefix,
        section.officeClear,
      );
    } else if (section?.officeClear?.tickets) {
      pushOfficeClearTicketRows(
        rows,
        `${labelPrefix} - Office Clear - Ticket Details`,
        section.officeClear.tickets,
      );
    }
    if (section?.utilityOfficeClear?.tickets) {
      pushOfficeClearTicketRows(
        rows,
        `${labelPrefix} - Utility Office Clear - Ticket Details`,
        section.utilityOfficeClear.tickets,
      );
    }
    if (isWater) {
      // if (section?.fieldMarked?.tickets) {
      //     pushTicketDetailRows(
      //         rows,
      //         `${labelPrefix} - Field Marked - Ticket Details`,
      //         section.fieldMarked.tickets
      //     );
      // }
      if (section?.fieldMarked?.ticketsByUtility) {
        pushTicketDetailRowsByUtility(
          rows,
          `${labelPrefix} - Field Marked - Ticket Details`,
          section.fieldMarked.ticketsByUtility,
        );
      }
      // if (section?.fieldCleared?.tickets) {
      //     pushTicketDetailRows(
      //         rows,
      //         `${labelPrefix} - Field Cleared - Ticket Details`,
      //         section.fieldCleared.tickets
      //     );
      // }
      if (section?.fieldCleared?.ticketsByUtility) {
        pushTicketDetailRowsByUtility(
          rows,
          `${labelPrefix} - Field Cleared - Ticket Details`,
          section.fieldCleared.ticketsByUtility,
        );
      }
    } else {
      // if (section?.fieldMarked?.tickets) {
      //     pushTicketDetailRows(
      //         rows,
      //         `${labelPrefix} - Field Marked - Ticket Details`,
      //         section.fieldMarked.tickets
      //     );
      // }
      if (section?.fieldMarked?.ticketsByUtility) {
        pushTicketDetailRowsByUtility(
          rows,
          `${labelPrefix} - Field Marked - Ticket Details`,
          section.fieldMarked.ticketsByUtility,
        );
      }
      // if (section?.fieldCleared?.tickets) {
      //     pushTicketDetailRows(
      //         rows,
      //         `${labelPrefix} - Field Cleared - Ticket Details`,
      //         section.fieldCleared.tickets
      //     );
      // }
      if (section?.fieldCleared?.ticketsByUtility) {
        pushTicketDetailRowsByUtility(
          rows,
          `${labelPrefix} - Field Cleared - Ticket Details`,
          section.fieldCleared.ticketsByUtility,
        );
      }
    }
  };

  const buildRoadsSheet = (roadsSection, title, labelPrefix) => {
    const rows = [];
    if (!roadsSection) return normalizeSheetRows(rows);
    pushRoadsSummary(rows, roadsSection, title);
    pushTicketDetails(rows, labelPrefix, roadsSection);
    return normalizeSheetRows(rows);
  };

  const buildWaterSheet = (waterSection, title, labelPrefix) => {
    const rows = [];
    if (!waterSection) return normalizeSheetRows(rows);
    pushWaterSummary(rows, waterSection, title);
    pushTicketDetails(rows, labelPrefix, waterSection, { isWater: true });
    return normalizeSheetRows(rows);
  };

  if (!data) {
    return [[""]];
  }

  // New response format: data.{roads_regional, roads_mto, water} => export as multi-sheet payload.
  if (data.roads_regional || data.roads_mto) {
    const sheets = {};
    if (data.roads_regional) {
      sheets["Roads - Regional"] = buildRoadsSheet(
        data.roads_regional,
        "ROADS (REGIONAL)",
        "ROADS (REGIONAL)",
      );
    }
    if (data.roads_mto) {
      sheets["Roads - MTO"] = buildRoadsSheet(
        data.roads_mto,
        "ROADS (MTO)",
        "ROADS (MTO)",
      );
    }
    if (data.water) {
      sheets["Water"] = buildWaterSheet(data.water, "WATER", "WATER");
    }
    return sheets;
  }

  // Legacy response format: data.{roads, water} => single-sheet output, preserving existing layout.
  const rows = [];
  const roads = data.roads;
  if (roads) {
    pushRoadsSummary(rows, roads, "ROADS");
  }

  // Empty row between sections (legacy behavior)
  rows.push(["", "", "", "", "", "", ""]);

  const water = data.water;
  if (water) {
    pushWaterSummary(rows, water, "WATER");
  }

  // Legacy ticket detail ordering: office clear first, then FM/FC, for both roads & water
  if (roads?.officeClear?.tickets) {
    pushOfficeClearTicketRows(
      rows,
      "ROADS - Office Clear - Ticket Details",
      roads.officeClear.tickets,
    );
  }
  pushWaterOfficeClearTicketSections(rows, "WATER", water?.officeClear);
  // if (roads?.fieldMarked?.tickets) {
  //     pushTicketDetailRows(
  //         rows,
  //         "ROADS - Field Marked - Ticket Details",
  //         roads.fieldMarked.tickets
  //     );
  // }
  if (roads?.fieldMarked?.ticketsByUtility) {
    pushTicketDetailRowsByUtility(
      rows,
      "ROADS - Field Marked - Ticket Details",
      roads.fieldMarked.ticketsByUtility,
    );
  }
  // if (roads?.fieldCleared?.tickets) {
  //     pushTicketDetailRows(
  //         rows,
  //         "ROADS - Field Cleared - Ticket Details",
  //         roads.fieldCleared.tickets
  //     );
  // }
  if (roads?.fieldCleared?.ticketsByUtility) {
    pushTicketDetailRowsByUtility(
      rows,
      "ROADS - Field Cleared - Ticket Details",
      roads.fieldCleared.ticketsByUtility,
    );
  }
  // if (water?.fieldMarked?.tickets) {
  //     pushTicketDetailRows(
  //         rows,
  //         "WATER - Field Marked - Ticket Details",
  //         water.fieldMarked.tickets
  //     );
  // }
  if (water?.fieldMarked?.ticketsByUtility) {
    pushTicketDetailRowsByUtility(
      rows,
      "WATER - Field Marked - Ticket Details",
      water.fieldMarked.ticketsByUtility,
    );
  }
  // if (water?.fieldCleared?.tickets) {
  //     pushTicketDetailRows(
  //         rows,
  //         "WATER - Field Cleared - Ticket Details",
  //         water.fieldCleared.tickets
  //     );
  // }
  if (water?.fieldCleared?.ticketsByUtility) {
    pushTicketDetailRowsByUtility(
      rows,
      "WATER - Field Cleared - Ticket Details",
      water.fieldCleared.ticketsByUtility,
    );
  }

  return normalizeSheetRows(rows);
};
